"use client";

import { useRef } from "react";
import {
  SectionHead,
  SectionShell,
  useBandRowAlign,
  useWholeRowBlocks,
} from "@/components/sections/kit";
import { useEnterStagger } from "@/components/sections/kit/motion";
import { LINK_DOMAIN, LINK_SLUG, PLAN_URL_PARTS } from "./data";


/** Plan — the UTM anatomy card + generic-vs-branded card. */
export default function PlanSection() {
  const rootRef = useRef<HTMLDivElement>(null);

  useWholeRowBlocks(rootRef);
  useBandRowAlign(rootRef);
  // The two cards form ONE visual row — rise together, no stagger.
  useEnterStagger(rootRef, "[data-plan-card]", { y: 26, stagger: 0 });

  return (
    <SectionShell id="plan" className="scroll-mt-[var(--grid-cell,72px)] md:pt-px md:pb-px">
      <div ref={rootRef} className="flex flex-col">
        <SectionHead
          eyebrow="Plan"
          title="One link. UTMs written once."
          lead="Set the campaign tag on the destination once with the UTM builder. Add ?utm_source per channel on the short link — every click records both, and one switch forwards the channel tag to your site."
        />
        <div data-grid-block className="grid items-stretch gap-4 max-md:mt-10 md:grid-cols-2">
          {/* What the URL actually says */}
          <div
            data-plan-card
            className="rounded-ref-lg border border-line bg-surface p-7 shadow-ref max-sm:p-5"
          >
            <div className="font-jetbrains text-[10.5px] uppercase tracking-[0.12em] text-dim">
              What the URL actually says
            </div>
            {/* The URL parts as an aligned stack — chip column + label
                column — so the dashed wire below visibly drains the whole
                stack into the one clean link (3 parts → 1 URL). */}
            <div className="mt-4 grid w-fit grid-cols-[max-content_minmax(0,1fr)] items-center gap-x-3.5 gap-y-2">
              {PLAN_URL_PARTS.map((u) => (
                <span key={u.label} className="contents">
                  <span
                    className={`whitespace-nowrap rounded-lg border px-3 py-1.5 font-jetbrains text-[11.5px] ${
                      u.on
                        ? "text-brand-700 [background:color-mix(in_srgb,var(--color-brand-600)_5%,white)] [border-color:color-mix(in_srgb,var(--color-brand-600)_30%,transparent)]"
                        : "border-line-2 bg-surface-2 text-muted"
                    }`}
                  >
                    {u.text}
                  </span>
                  <span
                    className={`font-jetbrains text-[9.5px] tracking-[0.08em] ${
                      u.on ? "text-brand-600" : "text-dim"
                    }`}
                  >
                    {u.label}
                  </span>
                </span>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <svg width="2" height="34" className="ml-[26px] overflow-visible" aria-hidden="true">
                <line
                  x1="1"
                  y1="2"
                  x2="1"
                  y2="32"
                  strokeWidth="1.5"
                  className="stroke-brand-600 [stroke-dasharray:3_5] animate-flow-dash"
                />
              </svg>
              <span className="font-jetbrains text-[10.5px] text-dim">
                what your audience sees instead
              </span>
            </div>
            <div className="inline-flex items-center gap-2.5 rounded-ref-sm border px-4 py-2.5 [background:color-mix(in_srgb,var(--color-brand-600)_6%,white)] [border-color:color-mix(in_srgb,var(--color-brand-600)_35%,transparent)]">
              <span className="font-jetbrains text-[13px]">
                <span className="font-bold text-brand-600">{LINK_DOMAIN}</span>
                {LINK_SLUG}
              </span>
              <span className="text-[11.5px] text-dim">— campaign tag stays hidden</span>
            </div>
            <p className="mt-[18px] text-[13px] leading-[1.6] text-muted [text-wrap:pretty]">
              Change the destination — or the campaign tag inside it — mid-campaign and every printed
              QR, bio link and newsletter button follows, because they all point at the link.
            </p>
          </div>

          {/* Generic vs. yours */}
          <div
            data-plan-card
            className="flex flex-col rounded-ref-lg border border-line bg-surface p-7 shadow-ref max-sm:p-5"
          >
            <div className="font-jetbrains text-[10.5px] uppercase tracking-[0.12em] text-dim">
              Generic vs. yours
            </div>
            <div className="mt-4 flex flex-col gap-2.5">
              <div className="flex items-center gap-3 rounded-ref-sm border border-line bg-surface-2 px-4 py-3">
                <span className="font-jetbrains text-[13px] text-dim">bit.ly/3xK9pQz</span>
                <span className="ml-auto text-right text-[11.5px] text-dim">
                  says nothing about you
                </span>
              </div>
              <div className="flex items-center gap-3 rounded-ref-sm border px-4 py-3 [background:color-mix(in_srgb,var(--color-brand-600)_5%,white)] [border-color:color-mix(in_srgb,var(--color-brand-600)_40%,transparent)]">
                <span className="font-jetbrains text-[13px]">
                  <span className="font-bold text-brand-600">{LINK_DOMAIN}</span>
                  {LINK_SLUG}
                </span>
                <span className="ml-auto text-right text-[11.5px] text-brand-700">
                  your brand, before the click
                </span>
              </div>
            </div>
            <p className="mt-[18px] text-[13px] leading-[1.6] text-muted [text-wrap:pretty]">
              A branded domain tells people who&apos;s behind the link before they commit a click —
              recognition a generic shortener can&apos;t earn. Same routing, same analytics, your
              name on the door.
            </p>
            <p className="mt-auto pt-3.5 font-jetbrains text-[11px] text-dim">
              works on rii.link out of the box — or connect your own domain via DNS
            </p>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
